/**
 * Push Notification Service
 * Sends real FCM push notifications to driver phones
 * Uses Supabase Edge Function as the server-side relay
 * 
 * SETUP REQUIRED:
 * 1. Deploy the 'send-push-notification' Supabase Edge Function (see setup guide)
 * 2. Set FIREBASE_SERVER_KEY in Supabase Edge Function secrets
 * 3. Ensure drivers table has fcm_token column populated by driver app
 */

import { supabase } from '../supabaseClient';

/**
 * Send a push notification to a specific driver
 * @param {string} driverId - Driver's ID from the drivers table
 * @param {string} title - Notification title
 * @param {string} body - Notification body text
 * @param {object} data - Optional data payload (e.g., { loadId: '...' })
 */
export const sendPushToDriver = async (driverId, title, body, data = {}) => {
  try {
    // Look up the driver's FCM token
    const { data: driver, error: driverError } = await supabase
      .from('drivers')
      .select('fcm_token, name')
      .eq('id', driverId)
      .single();

    if (driverError || !driver) {
      console.error('Driver not found:', driverError);
      return { success: false, error: 'Driver not found' };
    }

    if (!driver.fcm_token) {
      console.warn(`No FCM token for driver ${driver.name} — push skipped`);
      return { success: false, error: 'No FCM token', driverName: driver.name };
    }

    // Call the Supabase Edge Function
    const { data: result, error } = await supabase.functions.invoke('send-push-notification', {
      body: {
        token: driver.fcm_token,
        title,
        body,
        data,
      },
    });

    if (error) {
      console.error('Edge function error:', error);
      return { success: false, error: error.message };
    }

    console.log(`Push sent to ${driver.name}:`, title);
    return { success: true, driverName: driver.name, ...result };
  } catch (err) {
    console.error('Push notification error:', err);
    return { success: false, error: err.message };
  }
};

/**
 * Send push notifications to multiple drivers
 */
export const sendPushToMultipleDrivers = async (driverIds, title, body, data = {}) => {
  const results = await Promise.all(
    driverIds.map(id => sendPushToDriver(id, title, body, data))
  );

  return {
    total: results.length,
    successful: results.filter(r => r.success).length,
    failed: results.filter(r => !r.success).length,
    results,
  };
};

/**
 * Send a load assignment notification
 */
export const notifyLoadAssigned = async (driverId, load) => {
  const pickupStop = load.load_stops?.find(s => s.stop_type === 'pickup');
  const pickupAddr = pickupStop?.address || 'See app for details';

  return sendPushToDriver(
    driverId,
    `New Load: #${load.load_number}`,
    `Pickup: ${pickupAddr}\n${load.commodity || ''}`.trim(),
    { loadId: load.id, type: 'load_assigned' }
  );
};

/**
 * Send a load status change notification
 */
export const notifyStatusChange = async (driverId, load, newStatus) => {
  const statusLabels = {
    assigned: 'Load assigned to you',
    en_route: 'Status: En Route',
    at_pickup: 'Status: At Pickup',
    loading: 'Status: Loading',
    in_transit: 'Status: In Transit',
    at_delivery: 'Status: At Delivery',
    unloading: 'Status: Unloading',
    complete: 'Load completed!',
  };

  return sendPushToDriver(
    driverId,
    `Load #${load.load_number}`,
    statusLabels[newStatus] || `Status: ${newStatus}`,
    { loadId: load.id, type: 'status_change', status: newStatus }
  );
};

/**
 * Send a custom message to a driver
 */
export const notifyDriver = async (driverId, message) => {
  return sendPushToDriver(driverId, 'HM Hauling', message, { type: 'custom' });
};

/**
 * Check if FCM push is available for a driver
 */
export const hasPushToken = async (driverId) => {
  const { data, error } = await supabase
    .from('drivers')
    .select('fcm_token')
    .eq('id', driverId)
    .single();

  return !error && !!data?.fcm_token;
};

export default {
  sendPushToDriver,
  sendPushToMultipleDrivers,
  notifyLoadAssigned,
  notifyStatusChange,
  notifyDriver,
  hasPushToken,
};
